import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import {
  type Market,
  MARKETS,
  DEFAULT_MARKET,
  detectMarketCode,
  persistMarket,
  formatPrice as formatPriceFn,
} from "@/lib/currency";

interface CurrencyContextValue {
  market: Market;
  setMarket: (code: string) => void;
  formatPrice: (amountCents: number) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [market, setMarketState] = useState<Market>(() => {
    const code = detectMarketCode();
    return MARKETS[code] || DEFAULT_MARKET;
  });

  const setMarket = useCallback((code: string) => {
    const m = MARKETS[code];
    if (m) {
      setMarketState(m);
      persistMarket(code);
    }
  }, []);

  const formatPrice = useCallback(
    (amountCents: number) => formatPriceFn(amountCents, market),
    [market]
  );

  return (
    <CurrencyContext.Provider value={{ market, setMarket, formatPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
